import { useCalendar } from "./CalendarContext";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import cx from "classnames";
import styles from "./Calendar.module.scss";
import { DateTime, Info } from "luxon";
import { isBeyondMaxDate, isBeyondMinDate, isUnavailableDate } from "./utils";
import { useSwipe, useSwipeFn } from "../../hooks";
import { SrOnly } from "../SrOnly";
import { CalendarWeek } from "./CalendarWeek";

export type CalendarMonthProps = ComponentPropsWithoutRef<"table">;

/**
 * A component that displays a month in the calendar.
 *
 * @param props - The props for the table element.
 * @returns A component that displays a month in the calendar.
 */
export const CalendarMonth = forwardRef<HTMLTableElement, CalendarMonthProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    const context = useCalendar();
    const {
      value,
      focusedDate,
      setFocusedDate,
      setHoveredDate,
      locale,
      minDate,
      maxDate,
      unavailableDates,
      unavailable,
      defaultTimeZone,
      id: uid,
      range,
      dateFormat,
      startDay,
    } = context;

    const initialized = useRef(false);

    const ViewCX = cx(styles["grid"], className);
    const futureDateSelection = useCallback(
      (targetDate: DateTime) => {
        if (maxDate !== null && targetDate.toMillis() > maxDate?.toMillis()) {
          return;
        }

        if (
          isUnavailableDate(
            targetDate,
            unavailableDates,
            unavailable,
            defaultTimeZone,
          )
        ) {
          let nextFocusDate = targetDate.plus({ days: 1 });
          for (let i = 0; i < 365; i++) {
            if (
              maxDate !== null &&
              nextFocusDate.toMillis() > maxDate?.toMillis()
            ) {
              break;
            }
            if (
              isUnavailableDate(
                nextFocusDate,
                unavailableDates,
                unavailable,
                defaultTimeZone,
              )
            ) {
              nextFocusDate = nextFocusDate.plus({ days: 1 });
            } else {
              // setHoveredDate(nextFocusDate);
              setFocusedDate(nextFocusDate);
              break;
            }
          }
          return;
        }

        setFocusedDate(targetDate);
      },
      [maxDate, setFocusedDate, defaultTimeZone, unavailableDates, unavailable],
    );

    let startDayIndex = 6;
    switch (startDay) {
      case "Monday":
        startDayIndex = 0;
        break;
      case "Sunday":
      default:
        startDayIndex = 6;
    }

    const weekdaysArr: string[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (startDayIndex + i) % 7;
      weekdaysArr.push(Info.weekdays("narrow", { locale })[dayIndex]);
    }

    const firstDay = focusedDate.startOf("month");
    const lastDay = focusedDate.endOf("month");

    const monthArr = Array.from(new Array(firstDay.daysInMonth)).map((_, i) => {
      return firstDay.plus({ days: i });
    });

    const prevArr: DateTime<boolean>[] = [];
    if (firstDay.weekday !== startDayIndex + 1) {
      // Complete the first row with the previous month's dates.
      for (let i = 1; i < 8; i++) {
        const prevDay = firstDay.minus({ days: i });
        prevArr.push(prevDay);
        if (prevDay.weekday === startDayIndex + 1) break;
      }
    }
    prevArr.reverse();

    const nextArr = Array.from(new Array(14)).map((_, i) => {
      return lastDay.plus({ days: i + 1 });
    });

    const fullArr: DateTime[] = [...prevArr, ...monthArr, ...nextArr];

    const splitArr = [...Array(Math.ceil(fullArr.length / 7))]
      .map((_, i) => fullArr.slice(7 * i, 7 + 7 * i))
      .slice(0, 6);

    const onMouseLeaveHandler = () => {
      setHoveredDate(undefined);
    };

    const onSwipe = (dir: useSwipeFn["direction"]) => {
      if (dir === "left") {
        const targetMonth = focusedDate.plus({ month: 1 });
        if (
          isBeyondMinDate(targetMonth, minDate) ||
          isBeyondMaxDate(targetMonth, maxDate)
        )
          return;
        setFocusedDate(targetMonth);
        return;
      }
      if (dir === "right") {
        const targetMonth = focusedDate.minus({ month: 1 });
        if (
          isBeyondMinDate(targetMonth, minDate) ||
          isBeyondMaxDate(targetMonth, maxDate)
        )
          return;
        setFocusedDate(targetMonth);
        return;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- don't use direction
    const { direction, ...touchProps } = useSwipe(onSwipe);

    useEffect(() => {
      if (!initialized.current) return;
      setTimeout(() => {
        const targetElement = document.getElementById(
          `${uid}-${focusedDate.toISODate()}`,
        );
        targetElement?.focus();
      }, 100);
    }, [focusedDate, uid]);

    useEffect(() => {
      if (
        !isUnavailableDate(
          focusedDate,
          unavailableDates,
          unavailable,
          defaultTimeZone,
        )
      )
        return;
      futureDateSelection(focusedDate.plus({ days: 1 }));
    }, [
      focusedDate,
      futureDateSelection,
      defaultTimeZone,
      unavailableDates,
      unavailable,
    ]);

    return (
      <>
        <table
          ref={ref}
          data-anv="calendar-month"
          onFocus={() => {
            initialized.current = true;
          }}
          onBlur={() => {
            initialized.current = false;
          }}
          className={ViewCX}
          onMouseLeave={onMouseLeaveHandler}
          role="grid"
          {...rest}
          {...touchProps}
        >
          <thead aria-hidden className={styles["weekdays"]}>
            <tr>
              {weekdaysArr.map((weekday, i) => {
                return (
                  <th
                    scope="col"
                    key={`${weekday}${i}`}
                    className={styles["weekday"]}
                  >
                    {weekday}
                  </th>
                );
              })}
            </tr>
            <tr className={styles["spacer"]}>
              <td colSpan={7} />
            </tr>
          </thead>
          <tbody
            className={styles["day-grid"]}
            key={focusedDate.startOf("month").toMillis()}
          >
            {splitArr.map((week, i) => {
              return <CalendarWeek week={week} key={i} />;
            })}
          </tbody>
        </table>
        {range && value ? (
          <SrOnly aria-live="polite">
            {`Range starts at ${
              value.start && value.start.toLocaleString(dateFormat)
            },  ${
              value.end
                ? `ends at ${value.end.toLocaleString(dateFormat)}`
                : "select an end date"
            }`}
          </SrOnly>
        ) : null}
      </>
    );
  },
);

CalendarMonth.displayName = "CalendarMonth";
